// covered-by: frontends/carrie/src/components/memory-citation/__tests__/MemoryCitationChip.test.tsx

import { HoverCard } from "@radix-ui/themes";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { MEMORY_CHIP_CLASS, MemoryChipFace } from "./MemoryChipFace";
import { MemoryCitationPopover } from "./MemoryCitationPopover";
import { memoryTypeLabel } from "./memoryLabels";
import { popoverBoundary } from "./popoverBoundary";

import { type LinkableMemory } from "../../utils/memoryCitationFields";
import {
  acquireLit,
  memoryFocusKey,
  releaseLit,
  useMemoryFocus,
  useMemoryLitHandlers,
} from "../../utils/memoryCitationFocus";
import {
  SOURCE_HOVER_CLOSE_DELAY_MS,
  SOURCE_HOVER_OPEN_DELAY_MS,
} from "../ask-puntt-sources/SourceHoverCard";

/** How close the card may come to the edge of its boundary or the window. */
export const COLLISION_PADDING_PX = 16;

/**
 * The chip a saved memory leaves at the end of the sentence it supports: a
 * bookmark and "M1", "M2" ... numbered per answer.
 *
 * It sits beside the grey claim chips and is told apart from them by colour
 * alone (the memory teal), so a reader sees at a glance which sentences rest on
 * a registry rule and which on a saved note.
 *
 * Hover OR keyboard focus opens the detail card and Escape closes it. Tab from
 * the chip moves focus into the card, to "Open in Memories" (Radix keeps the
 * card's own controls out of the tab order, and the card sits at the end of the
 * page, so the keyboard would otherwise never reach that link); Escape there
 * closes the card and returns focus to the chip, and Tab goes on past the chip.
 * Hovering or focusing the chip also lights the sentence it supports
 * (`MemorySupport`) and its rail card.
 *
 * why the same primitive as the claim chips (`SourceHoverCard`): one hover
 * behaviour across every citation, including the open and close delays.
 */
export function MemoryCitationChip({
  memory,
  open: openProp,
  onOpenChange,
}: {
  memory: LinkableMemory;
  /** Controlled open, so a test can force the card visible. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation("ui");
  const key = memoryFocusKey(memory.scope, memory.id);
  const suffix = `${memory.scope}-${memory.id}`;
  const { lit } = useMemoryFocus(key);
  const litHandlers = useMemoryLitHandlers(key);
  const [uncontrolled, setUncontrolled] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolled;
  const chipRef = useRef<HTMLButtonElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const focusInCard = useRef(false);
  // why: Radix's hover openDelay can fire after the chip unmounts, and that setState runs with no document once the test environment is gone.
  // why re-arm in the body: StrictMode runs the cleanup and then the setup again, and a cleanup-only effect leaves the flag false so the card can never open in dev.
  // Stryker disable next-line BooleanLiteral: the mount effect below sets alive.current = true before any event or hover timer can call setOpen (React flushes passive effects before the next discrete event), so the ref's initial literal is never read.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const [boundary, setBoundary] = useState<Element | null>(null);
  const setOpen = (next: boolean) => {
    if (!alive.current) return;
    // why: moving focus from the chip into the card blurs the chip, which asks Radix to close the card a moment later; the card stays while focus is in it.
    if (!next && focusInCard.current) return;
    if (!controlled) setUncontrolled(next);
    onOpenChange?.(next);
  };
  const leaveCard = () => {
    focusInCard.current = false;
    chipRef.current?.focus();
  };
  const type = memoryTypeLabel(memory.type, t);

  // why held while the card is open: moving the pointer from the chip onto its card ends the chip's own hover, and the sentence would lose its tint while the reader is still reading what it rests on.
  useEffect(() => {
    if (!open) return undefined;
    acquireLit(key);
    return () => releaseLit(key);
  }, [open, key]);

  // why before paint: the card is placed within the answer column, and a first placement against the whole window would flash over the rail.
  useLayoutEffect(() => {
    if (open) setBoundary(popoverBoundary(chipRef.current));
  }, [open]);

  return (
    <span className="ml-1 inline-flex align-middle">
      <HoverCard.Root
        open={open}
        onOpenChange={setOpen}
        openDelay={SOURCE_HOVER_OPEN_DELAY_MS}
        closeDelay={SOURCE_HOVER_CLOSE_DELAY_MS}
      >
        <HoverCard.Trigger>
          <button
            {...litHandlers}
            ref={chipRef}
            type="button"
            data-testid={`memory-citation-chip-${suffix}`}
            data-lit={lit ? "true" : "false"}
            aria-label={
              type
                ? t("askPuntt.memoryCitation.chipAriaWithType", {
                    n: memory.number,
                    type,
                  })
                : t("askPuntt.memoryCitation.chipAria", { n: memory.number })
            }
            // why: touch has no hover, so a tap opens the card too.
            onClick={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key !== "Tab" || event.shiftKey || !open) return;
              const link = cardRef.current?.querySelector<HTMLElement>("a");
              if (!link) return;
              event.preventDefault();
              link.focus();
            }}
            className={MEMORY_CHIP_CLASS}
          >
            <MemoryChipFace number={memory.number} />
          </button>
        </HoverCard.Trigger>
        <HoverCard.Content
          ref={cardRef}
          onFocus={() => {
            focusInCard.current = true;
          }}
          onBlur={(event) => {
            if (
              !event.currentTarget.contains(event.relatedTarget as Node | null)
            ) {
              focusInCard.current = false;
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              leaveCard();
              setOpen(false);
            } else if (event.key === "Tab") {
              // Back goes to the chip; forward goes on from it, as the browser does.
              leaveCard();
              if (event.shiftKey) event.preventDefault();
            }
          }}
          side="top"
          align="start"
          sideOffset={8}
          collisionPadding={COLLISION_PADDING_PX}
          collisionBoundary={boundary ?? undefined}
          className="z-50 border-0 bg-transparent p-0 shadow-none"
        >
          <MemoryCitationPopover memory={memory} testIdSuffix={suffix} />
        </HoverCard.Content>
      </HoverCard.Root>
    </span>
  );
}
